/**
 * The quote register — `/countries/:cc/quotes` — as the WORKLIST (view B).
 *
 * ===========================================================================
 * WEB TIER A · WT3 — THE OWNER CHOSE VIEW B OF THE REGISTER ALTERNATIVES
 * ===========================================================================
 * The columnar table this file used to draw is retired. The chosen frame
 * (`register-alternatives.html`, altB24/altB3 — the owner overrode that file's
 * own recommendation of A) renders the register as a queue of decisions:
 * three bands derived from the rows on screen (open past the overdue
 * threshold / open this week / closed), two-line 52px rows whose whole row is
 * the link to the record, money on a fixed right rail, and a footer sentence
 * that names the grouping.
 *
 *  · THE BANDS ARE `features/quotes/worklist.ts` — pure, tested in isolation.
 *    Each band head's count is `band.rows.length`, reduced from the SAME array
 *    whose rows render beneath it. Never a second query.
 *  · NO `aria-sort`, NO sortable headers, NO `<table>`. Grouping and sorting
 *    are mutually exclusive — a grouped register still advertising `aria-sort`
 *    lies to a screen reader (the alternatives file's own §6 argument). The
 *    rows are lists under band-head headings; the old sr-only caption is now a
 *    heading, so AT still gets the scope sentence.
 *  · THE ROW'S SECOND LINE prints `docNo · type · raised {date}` for EVERY
 *    row, both types — view B's row grammar. The old table marked only the
 *    `tally_sales` exception; the tag ruling survives unchanged on OTHER
 *    surfaces, but this row's grammar is the mockup's, verbatim.
 *  · THE AGE ACCENT IS GONE, AND WITH IT ITS SECOND CLOCK. The retired
 *    `AgePill` tinted on time-in-status (`sent_at`); the worklist's structure
 *    reads raised-age only — the band titles say "raised" and mean it. The
 *    urgency signal moved from a per-row tint into the register's information
 *    architecture, which is the whole point of view B.
 *  · MASKED SEAT: any masked amount raises a permanent in-card banner naming
 *    the state, the identity reading it, and the remedy (the masked-frame
 *    treatment; the marker itself sits in the money's own slot at the money's
 *    size — zero reflow, and the banner is never hover-only).
 *
 * WHAT DID NOT MOVE: `StatusTabs` + `useQuoteCounts` (counts absent-not-zero
 * on refusal, the two "missing rather than wrong" alert sentences, tab-change
 * resets paging), the `listAnswer` five-state union, the honest pager, and
 * every V4/V5/V6 ruling below — the answer discipline is orthogonal to what
 * the rows look like.
 *
 * ===========================================================================
 * THE TAB COUNTS COST FOUR REQUESTS, AND THE SCREEN SAYS SO WHEN THEY FAIL
 * ===========================================================================
 * There is no `stage-counts` equivalent for quotes (see `use-quotes.ts`). The
 * counts therefore come from four `perPage: 1` list calls. When that query fails
 * the tabs render WITHOUT numbers rather than with zeros: a zero is a claim, and
 * "there are no drafts" is exactly the wrong thing to tell somebody whose count
 * request 500'd.
 */

import { ChevronRight, Plus, ShieldCheck } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCountryCode } from '../../auth/guards';
import type { CountryCode } from '../../domain/countries';
import { COUNTRIES } from '../../domain/countries';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, Tooltip } from '../../components/ui';
import { QUOTE_STATUS_MAP, QUOTE_TABS, QUOTE_TYPE_LABELS, daysSince, isQuoteStatus } from '../../domain/quotes';
import type { QuoteStatus } from '../../domain/quotes';
import { formatDateLong, isMaskedAmount } from '../inquiries/format';
import { cn } from '../../lib/cn';
import { ServerAmount } from './money';
import { QuoteStatusPill } from './status';
import { OutOfRangeNotice, QuotesEmptyState, WorklistSkeleton } from './states';
import { listAnswer } from './list-answer';
import { quoteErrorText } from './errors';
import { ageLabel, bandQuotes, pageScopeNotice } from './worklist';
import { useQuoteCounts, useQuoteList } from './use-quotes';
import type { Paged, QuoteDto } from './api';

const PER_PAGE = 50;

/**
 * The money rail: one class string shared by every money slot in the register,
 * so the last digit of every amount lands on the same x — the 0px-spread claim
 * the alternatives file measured against direction C. Fixed width, right
 * aligned, tabular figures, at the money's own size (`t-h3` in the frame).
 * A masked marker renders in this same slot at this same size: zero reflow.
 */
const MONEY_SLOT =
  'w-[180px] shrink-0 text-right text-h3 text-fg tnum';

/**
 * This same list at page 1 — every other search parameter kept, `page` dropped.
 *
 * A real href rather than a `goToPage(1)` button: it navigates, and this file
 * already argues (at the "New quote" action) that anything navigational must be
 * an anchor so middle-click and copy-link-address work. `SalesOrdersListPage`
 * carries the twin of this function for its own route.
 */
function firstPageHref(countryCode: string, params: URLSearchParams): string {
  const patch = new URLSearchParams(params);
  patch.delete('page');
  const query = patch.toString();
  return `/countries/${countryCode}/quotes${query === '' ? '' : `?${query}`}`;
}

export function QuotesListPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'quotes.view');
  const canCreate = can(countryCode, 'quotes.create');

  const rawStatus = params.get('status');
  const status: QuoteStatus | null = isQuoteStatus(rawStatus) ? rawStatus : null;
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const counts = useQuoteCounts(countryCode, canView);
  const list = useQuoteList(countryCode, { status, page, perPage: PER_PAGE }, canView);

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Quotes are not available to this account">
          Viewing quotes in {countryCode} needs the Quotes permission. Ask an administrator
          to widen your access.
        </Alert>
      </div>
    );
  }

  /*
   * ===========================================================================
   * V4 — ONE PAYLOAD PER RENDER. EVERY PAGING NUMBER BELOW COMES OFF THE ANSWER.
   * ===========================================================================
   * `useQuoteList` carries `placeholderData: keepPreviousData`, so from the click
   * until the answer lands `list.data` is still the PREVIOUS response while `page`
   * above is already the NEW url. Pairing a fresh URL page with a stale response
   * put three mutually exclusive claims in one DOM: leaving `?page=3` for page 2
   * rendered "Page 2 is past the end of this list" and "This list ends at page 2"
   * as adjacent sentences in one Alert, over a pager reading "51–60 of 60".
   *
   * The response states which page it describes — `{data, page, perPage, total}`,
   * and `paged()` in the API echoes the request rather than clamping it — so the
   * page number, the rows, the row count and the page size all come off ONE object
   * and cannot disagree WITH EACH OTHER. That is the whole of what reading one
   * payload buys, and it is worth having: it is what the two earlier fixes — an
   * unfiltered count probe, then `?? null` — failed to establish.
   *
   * ===========================================================================
   * WHAT ONE-PAYLOAD DOES NOT BUY. READ THIS BEFORE ATTEMPTING A SIXTH FIX.
   * ===========================================================================
   * An earlier draft of this block ended "There is no window left in which they
   * can disagree." That was FALSE AS WRITTEN, and a comment claiming a property
   * the code does not have is the thing that makes the next reader stop looking.
   *
   * Self-consistency is a property of the ANSWER. It says nothing about whether
   * the answer is to the question being asked, and under `keepPreviousData` it
   * routinely is not: the tab strip is driven by `status` off the URL, the rows
   * and the range are driven by the payload, and for one round trip those are two
   * different requests. No amount of reading-one-object closes that gap, because
   * it is not an inconsistency inside the payload — it is the payload answering a
   * question nobody is asking any more. `behind` closes it by MARKING.
   *
   * ===========================================================================
   * V6 — AND ONE PAYLOAD PER RENDER DID NOT BUY THIS EITHER: WHO MAY QUOTE IT.
   * ===========================================================================
   * V4 and V5 fixed which OBJECT the numbers came from. They left the object in
   * the page's own scope, so nodes rendered OUTSIDE the branch that decides what
   * is on screen went on quoting it after that branch had decided to show
   * something else. Under an errored refetch — reachable in production, because
   * `lib/query-client.ts` never retries a 4xx — `list.data` survives and
   * `isPlaceholderData` is FALSE, so neither the dim nor `behind` intervened: the
   * pager printed "1–50 of 60" with a live Next beneath "The quote list could not
   * be loaded", and the counts Alert called a list that was not there accurate.
   *
   * So the scope is gone. `listAnswer` returns ONE value whose payload fields
   * exist only on the variants that have a payload, and this file keeps no second
   * copy of them. Every claim about the rows below must therefore narrow `answer`
   * first — which is the compiler enforcing "decided by the same state that
   * decides the rows" on a node nobody has written yet. `list-answer.ts` carries
   * the full argument, including why this survives a sixth node.
   *
   * ===========================================================================
   * V5 — THE ROWS ARE VISIBLY BEHIND, NOT PRESENTED AS THE SELECTION'S ANSWER.
   * ===========================================================================
   * `isPlaceholderData` is true for exactly one reason: the payload was fetched
   * under a DIFFERENT query key than the one this render is asking for. So it is
   * the precise name of "the rows below do not belong to the selected tab or
   * page", and it rides on the `rows` variant because that is the only variant
   * that HAS rows to be wrong about.
   *
   * V4 marked only the EMPTY case (`holdingNothing`, now the `loading` variant).
   * With rows held, the screen went on presenting them as the selection's own
   * result — clicking Draft from All put `aria-selected="true"` and the count 5 on
   * Draft, over Confirmed- and Lost-pilled rows and a pager reading "1–50 of 60",
   * with nothing anywhere marking the screen busy. Four claims, three of them
   * about two different requests, all rendered as equally settled.
   *
   * THE TREATMENT IS W1'S, DELIBERATELY. `QuoteBuilderPage` already decided how
   * this app presents data that is BEHIND: one derived flag (`previewBehind`)
   * drives the opacity, `aria-busy`, the caption that stops asserting freshness,
   * and the control that would act on the stale value. A list is the same shape —
   * figures still true about an older request — so it gets the same treatment
   * rather than a second vocabulary. Blanking instead was rejected: that is what
   * `keepPreviousData` exists to prevent, and a register that empties on every
   * page step is a worse screen than one that dims.
   *
   * NOT `isFetching`, and this is the one place the two differ from W1's union. A
   * background refetch of the SAME key — a focus refetch, an invalidation after a
   * mutation — leaves rows that ARE this question's answer; dimming them under
   * "these are the previous result" would ship a fresh untrue sentence wearing a
   * loading state's clothes, which is precisely the failure mode this screen has
   * now produced five times. `isPlaceholderData` is the honest predicate.
   *
   * `behind` below is DERIVED FROM THE ANSWER and is the one thing in this file
   * safe to read without narrowing, because it is false by construction in every
   * state that has no rows — a load, a refusal, an empty page and an empty result
   * set are all "not behind" for the same reason: there is nothing on screen for
   * them to be behind WITH. It is still computed once, so the region, the caption
   * and the pager cannot reach different conclusions about one moment.
   *
   * THE TAB STRIP IS DELIBERATELY LEFT ALONE. It is the QUESTION, and it is true
   * from the instant it is clicked: the selection did take, and the number beside
   * it comes from `useQuoteCounts` — a separate query whose key carries no status
   * (`quoteKeys.counts(cc)`), so a tab change neither refetches nor invalidates
   * it and it is not behind anything. (An earlier note here called that query
   * "unfiltered". It is not: `use-quotes.ts` fires one unfiltered probe plus one
   * per tab, so four of the five numbers ARE filtered counts. The status-free KEY
   * is the load-bearing half and it is what this paragraph rests on.)
   * Dimming it would state that the click had not registered, which is a new false
   * claim of exactly the kind being removed. What was never true was the tab; it
   * was the unstated claim that the rows underneath ANSWER it, and that claim
   * lives in the answer region — so the answer region is what carries the mark.
   *
   * ===========================================================================
   * THE QUESTION IS THE URL, AND THESE THREE NODES DO RENDER OFF IT
   * ===========================================================================
   * `page` and `PER_PAGE` above are still correct, and are still used, FOR THE
   * REQUEST: the query key must ask for what the URL says or the address bar would
   * stop choosing the page.
   *
   * An earlier draft of this block ended "Nothing renders off the question." That
   * was untrue in its own file, forty lines below a paragraph saying the opposite.
   * THREE nodes render off the URL, all of them correctly, because all three are
   * statements ABOUT THE QUESTION rather than about the rows:
   *
   *  · `StatusTabs active={status}` — which tab was asked for. The click took;
   *    saying so is the one thing the strip may claim.
   *  · `QuotesEmptyState status/statusLabel` — which tab's emptiness is being
   *    described. Safe because `empty` and `pastEnd` CANNOT be placeholders (a
   *    rows-less placeholder is `loading`), so the URL tab and the payload are the
   *    same question there. That is a property of the union, not a coincidence.
   *  · `firstPageHref(countryCode, params)` — "this address minus `page`", which
   *    is a fact about the address bar and about nothing else.
   *
   * The rule that actually holds is narrower and is the one to keep: NO CLAIM
   * ABOUT THE ROWS RENDERS OFF THE QUESTION. `page` and `PER_PAGE` in particular
   * reach nothing below this line.
   */
  const answer = listAnswer(list);
  const behind = answer.kind === 'rows' && answer.behind;

  const setTab = (next: QuoteStatus | null): void => {
    const patch = new URLSearchParams(params);
    if (next === null) patch.delete('status');
    else patch.set('status', next);
    // A tab change resets paging. Keeping `page=3` while switching to a tab with
    // two rows shows an empty list that looks like an empty tab.
    patch.delete('page');
    setParams(patch, { replace: true });
  };

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Quotes"
        subtitle={
          counts.data === undefined
            ? undefined
            : `${String(counts.data.total)} ${counts.data.total === 1 ? 'quote' : 'quotes'} in ${countryCode}`
        }
        actions={
          /*
           * D5 — a gated action the caller cannot perform is ABSENT, not disabled.
           *
           * The caller this is about is MANAGEMENT, per
           * `apps/api/src/seeds/role-matrix.json`: they hold `quotes.view` (so they
           * are looking at this list) and not `quotes.create`. A greyed-out "New
           * quote" tells them the app is broken for them; no button tells them
           * raising quotes is not their job, which is the truth. (An earlier note
           * here said "a Developer" — Developer holds no quote permission at all
           * and never reaches this screen.)
           */
          canCreate ? (
            /*
             * A `<Link>` styled as the primary button, NOT a `<Button>` with an
             * onClick that navigates. It goes to a URL, so it must be a real
             * anchor: middle-click and copy-link-address are how somebody opens
             * the builder in a second tab, and `states.tsx` makes the same call
             * for the same reason.
             *
             * WT3: the fill is the D1a authored trio (`Button`'s own primary
             * classes), retiring this file's inline copy of the 4.22 `bg-indigo`
             * fill — WEB-TIER-A-LEDGER item 11, struck for this file.
             */
            <Link
              to={`/countries/${countryCode}/quotes/new`}
              className="inline-flex h-9 items-center gap-2 rounded-sm bg-indigo-strong px-4 text-body font-medium text-fg-inverse hover:bg-indigo-strong-hover active:bg-indigo-strong-active"
            >
              <Plus aria-hidden="true" className="size-4" />
              New quote
            </Link>
          ) : undefined
        }
      />

      <StatusTabs
        active={status}
        counts={counts.data?.byStatus ?? null}
        total={counts.data?.total ?? null}
        onSelect={setTab}
      />

      {counts.isError ? (
        <Alert tone="warning" title="The tab counts could not be loaded">
          {/*
           * V6 — THE SECOND SENTENCE IS A CLAIM ABOUT THE ROWS, SO THE ANSWER
           * MAKES IT.
           *
           * "The list below is still accurate." shipped unconditionally, beside a
           * `tone="warning"` title, at full opacity — which made it the most
           * confident sentence on a screen that was, at that moment, holding the
           * previous tab's rows under "These rows are the previous result", or
           * showing a refusal card with no list below at all. Two adjacent
           * sentences that cannot both be true, and the one asserting FRESHNESS
           * was the unmarked one.
           *
           * It is now emitted only by the one answer it is true of: settled rows.
           * Loading has no list below; `failed` has no list below; `pastEnd` and
           * `empty` have no rows to be accurate; and a `behind` answer already
           * says the opposite one node down. Both sentences are written out in
           * full rather than assembled from fragments — the house call recorded at
           * the Phase-3b banner in `SalesOrdersListPage`.
           *
           * THE SECOND HALF WAS UNCONDITIONAL, AND THAT WAS THE SAME BUG ONE
           * QUERY OVER. It read "the numbers beside each tab are MISSING rather
           * than wrong", on the reasoning that missing-ness is the only thing this
           * Alert independently knows. It is not: the numbers are rendered by
           * `StatusTabs` off `counts.data`, and `counts.data` SURVIVES a refused
           * refetch exactly as `list.data` does. So after a successful load and a
           * refused refresh, all five numbers sit on the strip while this sentence
           * calls them missing. Reproduced verbatim, counts 137/5/20/30/5 on screen
           * under "The numbers beside each tab are missing rather than wrong."
           *
           * `listAnswer` collapsed the LIST query; the counts query is the other
           * payload on this screen and was left with its own page-scope value. The
           * rule is the same either way: a sentence about what a node renders is
           * decided by the value THAT node renders from. So this half is decided by
           * `counts.data` — the very thing `StatusTabs` reads — and not by
           * `counts.isError`, which only knows that the last request failed.
           *
           * Four sentences, each written out in full rather than assembled from
           * fragments (the house call recorded at the Phase-3b banner), because
           * two independent facts vary: whether numbers are on the strip, and
           * whether the rows below are this question's settled answer.
           */}
          {counts.data === undefined
            ? answer.kind === 'rows' && !answer.behind
              ? 'The list below is still accurate. The numbers beside each tab are missing rather than wrong.'
              : 'The numbers beside each tab are missing rather than wrong.'
            : answer.kind === 'rows' && !answer.behind
              ? 'The list below is still accurate. The numbers beside each tab are from an earlier load rather than wrong.'
              : 'The numbers beside each tab are from an earlier load rather than wrong.'}
        </Alert>
      ) : null}

      {answer.kind === 'loading' ? (
        <WorklistSkeleton label="Loading quotes" />
      ) : answer.kind === 'failed' ? (
        <Alert tone="danger" title="The quote list could not be loaded">
          {quoteErrorText(answer.error)}
        </Alert>
      ) : (
        /*
         * V5 — THE ANSWER REGION. Everything inside is read off the one variant
         * that got this far, so all of it carries `behind` at once, in
         * `TotalsBlock`'s own two classes: opacity only, 150ms, inside
         * `motion-safe` (hard rule 4). One wrapper rather than a flag threaded
         * into three branches — the branches cannot then disagree about whether
         * the payload they are describing is current.
         */
        <div
          aria-busy={behind}
          className={cn(
            'flex flex-col gap-4',
            'motion-safe:transition-opacity motion-safe:duration-settle',
            behind && 'opacity-60',
          )}
        >
          {/*
           * W1's "the caption stops asserting freshness", in a sentence rather
           * than in a one-word label: a list has no single figure to caption, and
           * the dim alone does not say WHICH of the four numbers on screen is the
           * one that has not caught up.
           *
           * `behind` here implies rows on screen — it is true only on the `rows`
           * variant, and a rows-less placeholder is `loading` — so this only ever
           * sits over the worklist, and it can say "these rows" without hedging.
           */}
          {behind ? (
            <p className="text-label text-fg-secondary">
              These rows are the previous result — the list you asked for has not answered
              yet.
            </p>
          ) : null}

          {answer.kind === 'pastEnd' ? (
            /*
             * V3 — AN EMPTY PAGE IS NOT AN EMPTY RESULT SET.
             *
             * `rows.length === 0` alone sent `?page=3` of a 60-quote country to
             * the empty card, six nodes below a subtitle reading "60 quotes in
             * IN": the card said "No quotes yet — the first one starts with a
             * customer and a product." while the screen around it counted sixty.
             * The payload's `total` is THIS result set (the active tab), so `> 0`
             * means the rows exist and the page does not — navigation, not
             * emptiness. `states.tsx` holds the reasoning.
             *
             * V4 — the notice is a statement ABOUT A PAYLOAD, so it is fed from
             * one. V6 makes that structural rather than careful: `pastEnd` is the
             * only variant this branch can be entered from, and `shown` exists
             * only on the variants that landed a payload, so there is no longer a
             * spelling of this node that could read a page number off the URL.
             * `firstPageHref` is the one prop that stays on the URL, because it
             * BUILDS a URL — the way out is "this address minus `page`", which is
             * a fact about the address bar and not about the response.
             */
            <OutOfRangeNotice
              page={answer.shown.page}
              total={answer.shown.total}
              perPage={answer.shown.perPage}
              firstPageHref={firstPageHref(countryCode, params)}
            />
          ) : answer.kind === 'empty' ? (
            <QuotesEmptyState
              countryCode={countryCode}
              status={status}
              statusLabel={status === null ? 'All' : QUOTE_STATUS_MAP[status].label}
              totalInCountry={counts.data?.total ?? 0}
              canCreate={canCreate}
            />
          ) : (
            <QuoteWorklist countryCode={countryCode} rows={answer.rows} shown={answer.shown} />
          )}
        </div>
      )}

      {/*
       * V3 — THE PAGER IS A CONTROL OVER THE RESULT SET, SO IT IS GUARDED ON THE
       * RESULT SET.
       *
       * It used to be guarded on `rows.length === 0`, which is the same
       * rows-for-total confusion the empty card had: the one page where Previous
       * is the single most useful control — the one PAST the end — was the exact
       * page that hid it, and that is half of why this dead end had no way out.
       *
       * Nothing in range moves. Rows on screen implies `total > 0`, and a truly
       * empty result set is `total: 0`, where `Pager` returns `null` on its own
       * anyway. The single case that changed behaviour was
       * `total > 0 && rows.length === 0` — the defect.
       *
       * V3 ALSO WROTE, HERE, "pending and errored both leave `list.data`
       * undefined". THAT WAS FALSE, and it is the sentence that let F3 through:
       * an errored REFETCH keeps the last successful payload, so `list.data` is
       * very much defined in the error state. The premise is corrected rather than
       * deleted, because a guard is only as good as the claim it was written from.
       *
       * V4 — AND IT IS THE SAME RESULT SET THE ROWS CAME FROM.
       *
       * `page` and `perPage` are the RESPONSE's, not the URL's. The readout
       * computes `from = (page-1)*perPage+1`, so a URL page paired with the held
       * response printed "51–60 of 60" under page-1 rows on any ordinary Next —
       * the identical desync the notice had, one node lower. Reading both off
       * `shown` makes the readout describe the rows above it by construction, and
       * hands `goToPage` the page those rows actually are: the buttons are
       * `busy`-disabled for the whole in-flight window, so the only page they can
       * ever step from is a settled one.
       *
       * A rows-less placeholder drops the pager for that window too (it is the
       * `loading` variant). The screen is showing a skeleton because it has
       * nothing of this result set to show, and a pager is a control over a result
       * set — quoting the held one's total beneath a skeleton is the same category
       * of claim this fix exists to delete.
       *
       * V5 — AND WHEN THE ROWS ARE HELD, SO IS THE RANGE. `behind` is passed
       * rather than folded into the region above because `Pager` returns `null` on
       * a single-page list: wrapping it would leave an empty flex child and a
       * stray `gap-4` under every short list. Same flag, its own node — which is
       * W1's arrangement exactly (one `previewBehind`, read by the totals block
       * AND by the Save button).
       *
       * V6 — AND IT ONLY EXISTS WHERE THE RESULT SET DOES. Being its own node is
       * exactly what let it outlive the rows: an errored refetch leaves
       * `list.data` standing and `isPlaceholderData` false, so the guard
       * `shown === null || shown.total === 0` was satisfied by a payload the
       * branch above had already replaced with a refusal card, and the pager
       * printed "1–50 of 60" over it with `busy={list.isFetching}` false — both
       * buttons live, stepping through a list that was not on screen.
       *
       * The guard is now the ANSWER, and it is not a guard: `shown` exists only on
       * the two variants that landed a payload with a result set in it, so a pager
       * over `loading` or `failed` is not something this file can express. The
       * `empty` variant has `shown` too and is deliberately excluded — `total` is
       * 0 there, which `Pager` already renders as `null`; naming it here says so
       * once rather than leaving the reader to find that out downstream.
       */}
      {answer.kind === 'rows' || answer.kind === 'pastEnd' ? (
        <Pager
          page={answer.shown.page}
          perPage={answer.shown.perPage}
          total={answer.shown.total}
          onPage={goToPage}
          busy={list.isFetching}
          behind={behind}
        />
      ) : null}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── tabs ── */

/**
 * The segmented status tabs — the `Shell/Toolbar` (6:447) control, whose active
 * segment carries `shadow-tab-active` (the promoted token, Phase 2.5b).
 *
 * KEPT VERBATIM through the WT3 register rebuild: the tabs are the QUESTION and
 * the honest-count behaviour (absent, never zero) is a pinned claim. Yes, that
 * leaves two count-bearing structures on one screen — four tabs and up to three
 * band heads partitioning the same rows two ways. The alternatives file counts
 * that as view B's cost, and the owner chose B knowing it.
 */
function StatusTabs({
  active,
  counts,
  total,
  onSelect,
}: {
  active: QuoteStatus | null;
  counts: Readonly<Record<QuoteStatus, number>> | null;
  total: number | null;
  onSelect: (status: QuoteStatus | null) => void;
}): React.JSX.Element {
  const tabs: { key: QuoteStatus | null; label: string; count: number | null }[] = [
    { key: null, label: 'All', count: total },
    ...QUOTE_TABS.map((status) => ({
      key: status,
      label: QUOTE_STATUS_MAP[status].label,
      count: counts?.[status] ?? null,
    })),
  ];

  return (
    <div
      role="tablist"
      aria-label="Quote status"
      className="flex w-fit max-w-full items-center gap-1 overflow-x-auto rounded-sm border border-line bg-subtle p-1"
    >
      {tabs.map((tab) => {
        const selected = tab.key === active;
        return (
          <button
            key={tab.key ?? 'all'}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onSelect(tab.key)}
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm px-3 py-1.5 text-body',
              selected
                ? 'bg-surface font-medium text-fg shadow-tab-active'
                : 'text-fg-secondary hover:text-fg',
            )}
          >
            {tab.label}
            {/* Absent, not zero, when the counts query failed — see the file header. */}
            {tab.count === null ? null : (
              <span className="tnum text-label text-fg-secondary">{tab.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── the worklist ── */

/**
 * The register card: bands over two-line rows, a footer sentence, and — on a
 * masked seat — the banner that names the state inside the frame.
 */
function QuoteWorklist({
  countryCode,
  rows,
  shown,
}: {
  countryCode: CountryCode;
  rows: readonly QuoteDto[];
  shown: Paged<QuoteDto>;
}): React.JSX.Element {
  const bands = bandQuotes(rows);
  const masked = rows.some((row) => isMaskedAmount(row.grand_total));

  /*
   * The footer's second sentence — "Every amount is {currency}. Tax group
   * {name}." — is printed ONLY when the page's rows genuinely share one
   * currency and one tax group, both off the wire. A page that mixes either
   * simply omits the sentence (the honesty rule): the API carries the facts
   * per row, not per page, so a page-level claim is only makeable when the
   * rows agree unanimously.
   */
  const currencies = new Set(rows.map((row) => row.currency ?? ''));
  const taxGroups = new Set(rows.map((row) => row.tax_group_name ?? ''));
  const currency = currencies.size === 1 ? [...currencies][0] : '';
  const taxGroup = taxGroups.size === 1 ? [...taxGroups][0] : '';

  const from = (shown.page - 1) * shown.perPage + 1;
  const to = from + rows.length - 1;
  const scopeNotice = pageScopeNotice(rows.length, shown.total);

  return (
    <div className="overflow-hidden rounded-md border border-line bg-surface">
      {masked ? <MaskedBanner countryCode={countryCode} /> : null}
      {/*
       * The retired table's sr-only <caption>, moved to a heading — a grouped
       * list has no caption element, and the scope sentence still belongs to AT.
       */}
      <h2 className="sr-only">
        Quotes in {countryCode}, grouped by age, then raised newest first
      </h2>
      {bands.map((band) => (
        <section key={band.key}>
          {/*
           * The band head: a surface, not a whisper — `bg/subtle` carries
           * text/PRIMARY only (text/secondary there is the refused 4.40 pair).
           * The count beside the title is `band.rows.length` — the same array
           * mapped directly below, so the number cannot disagree with the rows.
           */}
          <header className="flex min-h-8 items-center justify-between gap-2.5 border-b border-line bg-subtle px-4 py-1.5">
            <h3 className="text-th uppercase text-fg">{band.title}</h3>
            <span className="tnum text-label font-medium text-fg">{band.rows.length}</span>
          </header>
          <ul className="m-0 list-none p-0">
            {band.rows.map((row) => (
              <WorklistRow key={row.id} countryCode={countryCode} row={row} />
            ))}
          </ul>
        </section>
      ))}
      <footer className="flex min-h-11 flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2 text-label text-fg-secondary">
        <p>
          {String(from)}–{String(to)} of {String(shown.total)}{' '}
          {shown.total === 1 ? 'quote' : 'quotes'}. Grouped by age, then raised newest
          first.
          {/*
           * L1-1: the bands partition THIS PAGE, and the server answers newest
           * first — so on a register longer than one page the alarm band under-
           * reports the backlog and can be absent from page 1 entirely. The
           * count beside each head is still exactly its own members; what the
           * reader could not otherwise know is the SCOPE those members were
           * drawn from. Printed only when the answer actually spans pages.
           */}
          {scopeNotice === null ? null : ` ${scopeNotice}`}
        </p>
        {currency !== '' && taxGroup !== '' ? (
          <p>
            Every amount is {currency}. Tax group {taxGroup}.
          </p>
        ) : null}
      </footer>
    </div>
  );
}

/**
 * One worklist row — 52px minimum, two lines, the WHOLE ROW one link to the
 * record (register-alternatives ruling 1: the chevron routes to a full page;
 * no peek panel, no drawer).
 *
 * THE LINK IS A STRETCHED OVERLAY, not a wrapper. Wrapping the row's content in
 * the anchor would put the tooltip triggers (focusable) INSIDE it — interactive
 * content inside `<a>` is invalid HTML and a keyboard trap. The overlay carries
 * the accessible name (customer first, then the number — the customer is what a
 * person recognises), covers the row for the pointer, and the few elements that
 * must stay hoverable/focusable sit above it (`z-[2]`).
 *
 * HOVER IS THE `_lib` RAIL TREATMENT: a 3px `accent/indigo` rail plus the
 * chevron rising from text/secondary to text/primary. No hover FILL — the
 * alternatives file measured that light mode has no legal one (`bg/subtle`
 * hover puts every quiet glyph in the row at 4.40) — and no opacity.
 *
 * THE AGE CELL's tooltip ("raised {date}") is the first vendored-Tooltip
 * consumer (WT2 handoff): supplementary phrasing — the raised date is already
 * printed on the row's second line, so the tooltip is never the only carrier.
 * Its trigger is the ROW LINK, the row's one focusable element, so keyboard
 * users get it without the register growing a second tab stop per row.
 */
function WorklistRow({
  countryCode,
  row,
}: {
  countryCode: CountryCode;
  row: QuoteDto;
}): React.JSX.Element {
  const customer = row.party_name ?? 'Unknown customer';
  const age = daysSince(row.created_at);
  const raised = formatDateLong(row.created_at);
  const masked = isMaskedAmount(row.grand_total);

  return (
    <li className="crf-register-row group relative flex items-center gap-3 border-b border-line px-4">
      {/* The hover/focus rail — decoration; the words carry the state. */}
      <span
        aria-hidden="true"
        className="invisible absolute inset-y-0 left-0 w-[3px] bg-indigo group-focus-within:visible group-hover:visible"
      />
      <Tooltip content={`raised ${raised}`}>
        <Link
          to={`/countries/${countryCode}/quotes/${row.id}`}
          aria-label={`${customer} — ${row.doc_no}`}
          className="absolute inset-0 z-[1] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo"
        />
      </Tooltip>

      {/* No `py-*` here: the row's height is `--crf-density-row` and `items-center`
        * does the centring. A vertical padding on this cell would put a second
        * authority on the row height and would make the compact stop unreachable
        * — measured at 50px against a 44px stop before it was removed. */}
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="truncate text-body font-medium text-fg">{customer}</span>
          <QuoteStatusPill status={row.status} />
        </div>
        {/*
         * Line 2 — view B's row grammar, for EVERY row: `docNo · type ·
         * raised {date}`. The type is printed on both kinds (the mockup's own
         * rows do), which supersedes the old table's mark-the-exception tag ON
         * THIS SURFACE only; `QuoteTypeTag` and its ruling survive elsewhere.
         */}
        <p className="mt-0.5 truncate text-label text-fg-secondary">
          <span className="font-mono text-mono">{row.doc_no}</span>
          {' · '}
          {QUOTE_TYPE_LABELS[row.type]}
          {' · raised '}
          {raised}
        </p>
      </div>

      {masked ? (
        /*
         * The masked marker, in the money's own slot at the money's size — zero
         * reflow — with the second vendored-Tooltip consumer: the explanation
         * surface. Supplementary by construction: the banner above the list
         * names the state, the identity and the remedy permanently, so this
         * tooltip repeats, never solely carries. It IS focusable (`tabIndex=0`,
         * above the overlay), and only masked rows pay that extra tab stop.
         */
        <Tooltip
          content={`Hidden — this account cannot view amounts in ${countryCode}. The notice above names the remedy.`}
        >
          <span tabIndex={0} data-slot="grand-total" className={cn(MONEY_SLOT, 'relative z-[2]')}>
            <ServerAmount value={row.grand_total} currency={row.currency} />
          </span>
        </Tooltip>
      ) : (
        <span data-slot="grand-total" className={MONEY_SLOT}>
          <ServerAmount value={row.grand_total} currency={row.currency} />
        </span>
      )}

      <span data-slot="age" className="w-24 shrink-0 text-right text-body13 text-fg-secondary tnum">
        {ageLabel(age)}
      </span>

      <ChevronRight
        aria-hidden="true"
        className="size-4 shrink-0 text-fg-secondary group-hover:text-fg"
      />
    </li>
  );
}

/**
 * The masked seat's banner — the fix for the screenshot defect the
 * alternatives file measured (§1.1): a register with the money deleted and
 * nothing inside the frame saying so. Three properties, all inside the pixels
 * and all visible in a crop: permanent (never hover-only), it names the
 * IDENTITY reading the screen (the commonest cause of an unexpected refusal is
 * being signed in as the wrong person), and it names the remedy.
 *
 * The identity clauses degrade honestly: a session still resolving (or a test
 * without one) simply omits the "signed in as" sentence rather than printing
 * a blank name. `status/info` tint with the `info/text` edge — nothing failed;
 * this seat is configured this way.
 */
function MaskedBanner({ countryCode }: { countryCode: CountryCode }): React.JSX.Element {
  const { me } = useSession();
  const role = me?.scopes.find((scope) => scope.c === countryCode)?.r ?? null;
  const countryName = COUNTRIES[countryCode].name;

  return (
    <p className="flex items-start gap-2.5 border-b border-info-fg bg-info-bg px-4 py-3 text-body13 text-info-fg">
      <ShieldCheck aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <span>
        Amounts are hidden for this account.{' '}
        {me == null
          ? ''
          : role === null
            ? `You are signed in as ${me.name}. `
            : `You are signed in as ${me.name}, ${role} for ${countryName}. `}
        No built-in role reads quotes without amounts, so this is a per-user permission
        override and an administrator can restore it.
      </span>
    </p>
  );
}

/* ─────────────────────────────────────────────────────────────────── pager ── */

function Pager({
  page,
  perPage,
  total,
  onPage,
  busy,
  behind,
}: {
  page: number;
  perPage: number;
  total: number;
  onPage: (page: number) => void;
  /** A request is in flight, so the buttons must not step from a moving page. */
  busy: boolean;
  /**
   * V5. The range below describes a payload fetched for a DIFFERENT question —
   * the tab or page the URL has already moved off. The numbers are still true
   * about that payload and are still the rows on screen, so they are marked
   * rather than blanked: `TotalsBlock`'s treatment, for the same reason.
   */
  behind: boolean;
}): React.JSX.Element | null {
  const pages = Math.max(1, Math.ceil(total / perPage));
  if (pages === 1) return null;
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  return (
    <div
      aria-busy={behind}
      className={cn(
        'flex items-center justify-between gap-3',
        'motion-safe:transition-opacity motion-safe:duration-settle',
        behind && 'opacity-60',
      )}
    >
      <p className="tnum text-label text-fg-secondary">
        {/*
         * V3. The range readout is only true for a page that HAS rows. Now that
         * the pager renders past the end too, `from`–`to` reads "101–60 of 60",
         * which is a fresh lie printed to escort the reader out of the old one.
         * Past the end the row range is not a fact this screen holds, so it says
         * the one number it does.
         */}
        {from > total
          ? `${String(total)} in total`
          : `${String(from)}–${String(to)} of ${String(total)}`}
      </p>
      <div className="flex items-center gap-2">
        <Button variant="ghost" disabled={page <= 1 || busy} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <Button variant="ghost" disabled={page >= pages || busy} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}
